import { ComponentType, createContext, useCallback, useContext } from 'react';
import useValidation from './useValidation';

export type TFormValue = ReturnType<typeof useValidation>[1];

const FormContext = createContext<IFormValue>(null as unknown as IFormValue);

export type TErrors = Record<string, string[] | undefined> | undefined;
export interface IFormValue {
  /** The form data */
  data: TFormValue['data'];
  /** The errors associated with the current form data */
  errors: TFormValue['errors'];
  /** Used by inputs and sections to update our state and call field-by-field validation */
  onChange: TFormValue['onChange'];
  /** Takes field-by-field validators and links them back to this so we can validate the whole form */
  registerValidators?: TFormValue['registerValidators'];
  /** A function that removes a currently registered validator in the useValidation hook */
  unregisterValidators?: TFormValue['unregisterValidators'];
  /** Used by FormSection and ListFormSection to link validation back to the root controller,
   * can also be used for complex inputs
   */
  registerSelfValidatingChild?: TFormValue['registerSelfValidatingChild'];
  /** Resets the data state in useValidation to the current data prop or initialData prop */
  resetData?: TFormValue['resetData'];
  /** Resets the error state in useValidation to the initialErrors prop */
  resetErrors?: TFormValue['resetErrors'];
  /** Removes the connection of a self validating child to the useValidation hook */
  unregisterSelfValidatingChild?: TFormValue['unregisterSelfValidatingChild'];
  /** Validates the entire field or just the section/field at `name` */
  validate?: TFormValue['validate'];
}

export const getOnChange =
  (onChange: IFormValue['onChange'], onChangeProp?: (...args: unknown[]) => void) =>
  (...args: [string, unknown, boolean]) => {
    onChange?.(...args);

    if (onChangeProp) {
      onChangeProp(...args);
    }
  };

export function withFormContext<TProps extends { onChange?(...args: unknown[]): void }>(
  WrappedComponent: ComponentType<TProps>
) {
  return function FormContextConsumer(props: TProps) {
    const { onChange, ...value } = useContext(FormContext);
    const { onChange: propsOnChange } = props;

    return (
      <WrappedComponent
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...value}
        onChange={useCallback(getOnChange(onChange, propsOnChange), [propsOnChange])}
      />
    );
  };
}

export default FormContext;
